import { useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { useChoices, useServiceOrdersExport, useServiceOrdersList } from "../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { EyeIcon, FilterX, AlertCircle } from "lucide-react";
import { Input } from "@shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Button } from "@shared/ui/button";
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { useNavigate } from "react-router-dom";
import { logEvent } from "@shared/analytics/analytics";

// Types
export type WorkOrderStatus =
  | "CREATED"
  | "ASSIGNED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "APPROVED"
  | "PENDING";

interface WorkOrderListProps {
  status: WorkOrderStatus | "ALL";
}

interface WorkOrderFilters {
  startDate: string;
  endDate: string;
  operationType: any | null;
}

const WorkOrderList = ({ status }: WorkOrderListProps) => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = Number(searchParams.get("page")) || 1;
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const operationType = searchParams.get("operationType") || "";

  const [pendingFilters, setPendingFilters] = useState<WorkOrderFilters>({
    startDate: decodeURIComponent(startDate),
    endDate: decodeURIComponent(endDate),
    operationType: decodeURIComponent(operationType) || null,
  });

  // Add validation state
  const [dateValidationError, setDateValidationError] = useState<string>("");

  useEffect(() => {
    setPendingFilters({
      startDate: decodeURIComponent(startDate),
      endDate: decodeURIComponent(endDate),
      operationType: decodeURIComponent(operationType) || null,
    });
  }, [startDate, endDate, operationType]);

  // Date validation function
  const validateDateRange = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return "";

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      return "Start date cannot be greater than end date";
    }

    return "";
  };

  // Enhanced filter change handler with validation
  const handlePendingFilterChange = (
    key: keyof WorkOrderFilters,
    value: string
  ) => {
    const newFilters = {
      ...pendingFilters,
      [key]: value === "" ? null : value,
    };

    setPendingFilters(newFilters);

    // Validate date range when either date changes
    if (key === "startDate" || key === "endDate") {
      const startDateValue =
        key === "startDate" ? value : pendingFilters.startDate;
      const endDateValue = key === "endDate" ? value : pendingFilters.endDate;

      const error = validateDateRange(startDateValue, endDateValue);
      setDateValidationError(error);
    }
  };

  const handleApplyFilters = () => {
    // Check for date validation errors before applying
    const validationError = validateDateRange(
      pendingFilters.startDate,
      pendingFilters.endDate
    );

    if (validationError) {
      setDateValidationError(validationError);
      return; // Don't apply filters if validation fails
    }

    const newParams = new URLSearchParams(searchParams);

    // Reset to first page when applying filters
    newParams.set("page", "1");

    if (pendingFilters.startDate) {
      newParams.set("startDate", pendingFilters.startDate);
    } else {
      newParams.delete("startDate");
    }

    if (pendingFilters.endDate) {
      newParams.set("endDate", pendingFilters.endDate);
    } else {
      newParams.delete("endDate");
    }

    if (pendingFilters.operationType) {
      newParams.set("operationType", pendingFilters.operationType);
    } else {
      newParams.delete("operationType");
    }

    setSearchParams(newParams);
    setDateValidationError(""); // Clear any previous errors
  };

  const resetFilters = () => {
    const newParams = new URLSearchParams();
    // Only keep non-filter params if needed
    newParams.set("page", "1");

    setSearchParams(newParams);
    setDateValidationError(""); // Clear validation errors
  };

  const handleSearch = (searchTerm: string) => {
    const newParams = new URLSearchParams(searchParams);

    // Reset to first page when searching
    newParams.set("page", "1");

    if (searchTerm.trim()) {
      newParams.set("search", searchTerm.trim());
    } else {
      newParams.delete("search");
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", String(page));
    setSearchParams(newParams);
  };

  const queryParams = useMemo(
    () => ({
      remoteUtilityId,
      page: currentPage,
      limit: PAGE_SIZE,
      ...(status !== "ALL" && { status }),
      ...(searchQuery && { searchData: searchQuery }),
      ...(startDate && {
        created_date_gte: format(new Date(startDate), "yyyy-MM-dd"),
      }),
      ...(endDate && {
        created_date_lte: format(new Date(endDate), "yyyy-MM-dd"),
      }),
      ...(operationType && {
        operation_type: Number(operationType),
      }),
    }),
    [
      remoteUtilityId,
      currentPage,
      status,
      searchQuery,
      startDate,
      endDate,
      operationType,
    ]
  );

  const { data: list } = useServiceOrdersList(queryParams);
  const { data: sopTypes } = useChoices({
    choice: "service_order_operation_type",
  });
  const { exportCSV, isExporting } = useServiceOrdersExport(queryParams);

  const processedData = useMemo(() => {
    if (!list?.results) return [];

    return list.results.map((sop) => {
      const consumer = sop?.additionalData?.consumerInfo;
      const isEmptyConsumer = !consumer || Object.keys(consumer).length === 0;

      const consumerName = isEmptyConsumer
        ? "-"
        : `${consumer?.firstName ?? ""} ${consumer?.lastName ?? ""}`.trim();

      return {
        ...sop,
        consumerName,
        id: sop.id || sop.jobCardNo,
      };
    });
  }, [list?.results]);

  const getMeterServiceOrderRoute = (
    statusDisplay: string,
    jobCardNo: string
  ): string | null => {
    const status = statusDisplay?.toLowerCase();

    switch (status) {
      case "created":
        return `/service-orders/dispatcher/pending-meter-service-order/${jobCardNo}`;
      case "assigned":
        return `/service-orders/dispatcher/assigned-meter-service-order/${jobCardNo}`;
      case "pending":
        return `/service-orders/dispatcher/pending-meter-service-order/${jobCardNo}`;
      case "completed":
        return `/service-orders/dispatcher/completed-meter-service-order/${jobCardNo}`;
      case "approved":
        return `/service-orders/dispatcher/history-service-order/${jobCardNo}`;
      default:
        return `/service-orders/dispatcher/pending-meter-service-order/${jobCardNo}`;
    }
  };
  const handleViewWorkOrder = (workOrder: any) => {
    const { id, statusDisplay } = workOrder;
    const route = getMeterServiceOrderRoute(statusDisplay, id);
    logEvent("MX Service Order viewed", {
    orderId: id,
  });
    if (route) {
      navigate(route);
    }
  };

  const columns: TableColumn<any>[] = [
    {
      key: "jobCardNo",
      header: "Order",
      sortable: true,
    },
    {
      key: "sopOperationTypeDisplay",
      header: "Type",
      type: "badge",
      badgeConfig: {
        variant: "secondary",
      },
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getClassName: (value) => {
          switch (value?.toLowerCase()) {
            case "created":
              return "bg-blue-50 text-blue-600";
            case "in_progress":
            case "assigned":
              return "bg-purple-50 text-purple-600";
            case "pending":
              return "bg-yellow-50 text-yellow-600";
            case "completed":
              return "bg-green-50 text-green-600";
            default:
              return "bg-gray-100 text-gray-600";
          }
        },
      },
    },
    {
      key: "requestAccountNo",
      header: "Resource",
      sortable: true,
    },
    {
      key: "consumerName",
      header: "Consumer",
      sortable: true,
    },
    {
      key: "sopUtilityService",
      header: "Utility Service",
    },
    {
      key: "createdDate",
      header: "Created",
      sortable: true,
    },
    {
      key: "createdUserRemoteName",
      header: "Created By",
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View",
      icon: "Eye",
      onClick: handleViewWorkOrder,
    },
  ];

  const pagination = list
    ? {
        currentPage,
        totalPages: Math.ceil(list.count / PAGE_SIZE),
        totalItems: list.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, list.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Start Date</label>
          <Input
            type="date"
            value={pendingFilters.startDate}
            onChange={(e) =>
              handlePendingFilterChange("startDate", e.target.value)
            }
            className={`w-full ${dateValidationError ? "border-red-500" : ""}`}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">End Date</label>
          <Input
            type="date"
            value={pendingFilters.endDate}
            onChange={(e) =>
              handlePendingFilterChange("endDate", e.target.value)
            }
            className={`w-full ${dateValidationError ? "border-red-500" : ""}`}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Service Type</label>
          <Select
            value={pendingFilters.operationType || ""}
            onValueChange={(value) =>
              handlePendingFilterChange("operationType", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Service Type" />
            </SelectTrigger>
            <SelectContent>
              {sopTypes && sopTypes.length > 0 ? (
                sopTypes.map((sop) => (
                  <SelectItem key={sop.key} value={String(sop.key)}>
                    {sop.value}
                  </SelectItem>
                ))
              ) : (
                <SelectItem disabled value="no-data">
                  No data available
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Validation Error Message */}
      {dateValidationError && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-md">
          <AlertCircle className="h-4 w-4" />
          <span>{dateValidationError}</span>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleApplyFilters}
          disabled={!!dateValidationError}
        >
          Apply Filters
        </Button>

        <Button
          variant="ghost"
          onClick={resetFilters}
          className="flex items-center gap-2"
        >
          <FilterX className="h-4 w-4" />
          Clear Filters
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters = startDate || endDate || operationType || searchQuery;
  const handleExport = () => {
    exportCSV();
  };

  return (
    <div className="space-y-4">
      <GenericTable
        data={processedData}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchValue={searchQuery} // Pass current search value from URL
        searchPlaceholder="Search work orders..."
        filterPanel={filterPanel}
        onExport={handleExport}
        exportLoading={isExporting}
        emptyMessage="No work orders found"
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {
                  Object.values({
                    startDate,
                    endDate,
                    operationType,
                    search: searchQuery,
                  }).filter(Boolean).length
                }{" "}
                filter(s) active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default WorkOrderList;
